import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Button, IconButton, Tooltip, Typography,
  useMediaQuery, useTheme, Avatar
} from '@mui/material';
import {
  FiEdit, FiTrash2, FiEye, FiPlus, FiDownload, FiArrowLeft,
  FiBookOpen, FiUser
} from 'react-icons/fi';
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import AddStudentModal from './AddStudentModal';
import EditStudentModal from './EditStudentModal';
import api from '../../api';
import Swal from 'sweetalert2';


const ManageStudentsSection = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [studentToEdit, setStudentToEdit] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        //const response = await axios.get(`${API_URL}/api/users`);
        const response = await api.get('/users'); // no need for base URL
        console.log("Fetched students:", response.data);
        const baseURL = api.defaults.baseURL.replace('/api', ''); // remove /api from baseURL
        // Map API data to DataGrid format if needed
        const mapped = response.data.map(user => ({
          id: user.id,
          name: user.name,
          email: user.email,
          contact: user.phone || '—',
          photo: user.profile_image
            ? `${baseURL}/storage/profile_images/${user.profile_image}`
            : 'https://ui-avatars.com/api/?name=Student&background=ddd',

          last_active: user.last_active || '—',
          status: user.status === 1 ? 'Active' : 'Inactive' // ✅ Map status here
        }));


        setStudents(mapped);
      } catch (err) {
        console.error('Fetch failed:', err);
        setError('Failed to load students');
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, []);


  const handleDelete = async (id) => {
  const confirm = await Swal.fire({
    title: "Are you sure?",
    text: "This student will be permanently deleted!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Yes, delete it!",
  });

  if (confirm.isConfirmed) {
    try {
      await api.delete(`/users/${id}`);
      setStudents(prev => prev.filter(s => s.id !== id));

      Swal.fire("Deleted!", "The student has been removed successfully.", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to delete student", "error");
    }
  }
};



  const handleAddStudent = (newStudent) => {
    setStudents(prev => [...prev, { id: Date.now(), ...newStudent }]);
  };

const handleUpdateStudent = async (updatedStudent) => {
  try {
    const response = await api.put(`/users/${updatedStudent.id}`, updatedStudent);
    const updatedUser = response.data.user; // Extract the user object

    const baseURL = api.defaults.baseURL.replace('/api', '');
    const mappedUser = {
      id: updatedUser.id,
      name: updatedUser.name,
      email: updatedUser.email,
      contact: updatedUser.phone || '—',
      photo: updatedUser.profile_image
        ? `${baseURL}/storage/profile_images/${updatedUser.profile_image}`
        : 'https://ui-avatars.com/api/?name=Student&background=ddd',
      last_active: updatedUser.last_active || '—',
      status: updatedUser.status ? 'Active' : 'Inactive'
    };

    // Update the students state
    setStudents(prev =>
      prev.map(student =>
        student.id === mappedUser.id ? mappedUser : student
      )
    );

    Swal.fire({
      icon: 'success',
      title: 'Updated!',
      text: 'Student details updated successfully.',
      timer: 1500,
      showConfirmButton: false,
    });

    setStudentToEdit(null); // Close modal
  } catch (error) {
    console.error("Update failed:", error);
    Swal.fire({
      icon: 'error',
      title: 'Update Failed',
      text: error.response?.data?.message || 'Something went wrong!',
    });
  }
};

  const handleExportCSV = () => {
    const headers = ['Name', 'Email', 'Contact', 'Last Active'];
    const rows = students.map(s => [s.name, s.email, s.contact, s.last_active]);

    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += headers.join(',') + '\n';
    rows.forEach(row => { csvContent += row.join(',') + '\n'; });
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'students.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      field: 'photo',
      headerName: 'Photo',
      width: 80,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" justifyContent="center" paddingLeft={1}>
          <Avatar
            src={params.row.photo || 'https://ui-avatars.com/api/?name=Student&background=ddd'}
            alt={params.row.name}
            sx={{ width: 40, height: 40 }}
          />
        </Box>
      )
    },

    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    {
      field: 'contact',
      headerName: 'Contact',
      flex: 1,
    },
    {
      field: 'last_active',
      headerName: 'Last Active',
      flex: 1,
      renderCell: (params) => (
        <span>{params.value !== '—' ? params.value : '—'}</span>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: params.value === 'Active' ? 'green' : 'red',
            fontWeight: 600,
            pt: '6px'
          }}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View">
            <IconButton color="primary">
              <FiEye />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton color="success" onClick={() => setStudentToEdit(params.row)}>
              <FiEdit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton color="error" onClick={() => handleDelete(params.row.id)}>
              <FiTrash2 />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

 const filteredStudents = students.filter(student =>
  (student.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
  (student.email || "").toLowerCase().includes(searchTerm.toLowerCase())
);


  const CustomToolbar = () => (
    <GridToolbarContainer>
      <Button
        variant="outlined"
        color="info"
        startIcon={<FiDownload />}
        onClick={handleExportCSV}
        sx={{ m: 1 }}
      >
        Export CSV
      </Button>
    </GridToolbarContainer>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        padding: 2,
        boxSizing: 'border-box',
      }}
    >
      {/* Header and Action Buttons */}
      <Box
        mb={3}
        display="flex"
        flexDirection={isSmallScreen ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        textAlign={isSmallScreen ? 'center' : 'left'}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            borderRadius: '12px',
            p: '8px 12px',
            background: 'linear-gradient(135deg, #00bcd4, #007bff)',
            color: '#fff',
            '&:hover': {
              background: 'linear-gradient(135deg, #007bff, #00bcd4)',
              transform: 'scale(1.05)'
            }
          }}
        >
          <FiArrowLeft />
          {!isSmallScreen && <Typography variant="button" sx={{ ml: 1, fontWeight: 600 }}>Back</Typography>}
        </IconButton>

        <Box display="flex" alignItems="center" justifyContent="center" gap={1} flex={1}>
          <Box sx={{
            fontSize: '2rem',
            background: '#e3f2fd',
            borderRadius: '50%',
            p: 1,
            color: '#1976d2',
            animation: 'pulseIcon 1.6s infinite',
          }}>
            <FiUser />
          </Box>
          <Typography
            variant={isSmallScreen ? 'h6' : 'h5'}
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(90deg, #007BFF, #00d4ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textTransform: 'uppercase',
              letterSpacing: '1.2px',
              px: 1
            }}
          >
            Manage Students
          </Typography>
        </Box>

        <Button
          fullWidth={isSmallScreen}
          variant="contained"
          color="warning"
          startIcon={<FiPlus />}
          onClick={() => setIsModalOpen(true)}
        >
          Add Student
        </Button>
      </Box>


      {/* Search Box */}
      {/* <Box mb={2}>
      <input
        type="text"
        placeholder="Search by name or email"
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        style={{
          width: '100%',
          padding: '10px 12px',
          fontSize: '1rem',
          border: '1px solid #ccc',
          borderRadius: '8px'
        }}
      />
    </Box> */}

      {/* Table Section (Flex Grow) */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          width: '100%',
          backgroundColor: '#fff',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGrid
          rows={filteredStudents}
          columns={columns}
          pagination
          pageSize={25}
          rowsPerPageOptions={[10, 25, 50]}
          getRowId={(row) => row.id}
          checkboxSelection
          disableSelectionOnClick
          loading={loading}
          onSelectionModelChange={(ids) => setSelectedRows(ids)}
          components={{ Toolbar: CustomToolbar }}
          sx={{
            flexGrow: 1,
            fontFamily: 'Segoe UI',
            border: '1px solid #ddd',
            boxShadow: 2,
            '& .MuiDataGrid-columnHeaders': {
              background: 'linear-gradient(to right, #e0f3ff, #d5faff)',
              fontWeight: 'bold'
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.95rem'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#fef7e0'
            }
          }}
        />
      </Box>

      {/* Modals */}
      {/* {isModalOpen && (
        <AddStudentModal
          onClose={() => setIsModalOpen(false)}
          onAddStudent={handleAddStudent}
        />
      )} */}

      <AddStudentModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAddStudent={handleAddStudent}
      />


      {studentToEdit && (
        <EditStudentModal
          open={Boolean(studentToEdit)}
          student={studentToEdit}
          onClose={() => setStudentToEdit(null)}
          onSave={handleUpdateStudent}
        />
      )}

    </Box>
  );
}

export default ManageStudentsSection;
